import { defineStore } from 'pinia'
import { ref } from 'vue'
import { request } from '@/utils/request'

const TOKEN_KEY = 'prompt-gallery-token'
const PROFILE_KEY = 'prompt-gallery-profile'

/**
 * @description 客户端登录用户 store（独立于后台）
 *  - token: 客户端 JWT（type=client）
 *  - profile: 当前登录的客户端用户信息
 *  - 接口走 /auth-client/*（与后台 /auth/* 完全隔离）
 */
export const useUserStore = defineStore('user', () => {
  const token = ref<string>(localStorage.getItem(TOKEN_KEY) || '')
  const profile = ref<IClientUser | null>(loadProfile())

  function loadProfile(): IClientUser | null {
    try {
      const raw = localStorage.getItem(PROFILE_KEY)
      if (raw) return JSON.parse(raw)
    } catch {}
    return null
  }

  function persistProfile() {
    if (profile.value) {
      localStorage.setItem(PROFILE_KEY, JSON.stringify(profile.value))
    } else {
      localStorage.removeItem(PROFILE_KEY)
    }
  }

  function setToken(t: string) {
    token.value = t
    localStorage.setItem(TOKEN_KEY, t)
  }

  function logout() {
    token.value = ''
    profile.value = null
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(PROFILE_KEY)
  }

  const isLogged = () => !!token.value

  /**
   * OAuth 登录完成后调用（token 与 profile 由 OAuthCallback.vue 写入）
   */
  async function loginWithToken(accessToken: string, user: IClientUser) {
    setToken(accessToken)
    profile.value = user
    persistProfile()
  }

  /**
   * 获取当前登录用户信息（从 /auth-client/account）
   */
  async function fetchProfile(): Promise<IClientUser | null> {
    if (!token.value) return null
    try {
      const res = await request<IClientUser>({ url: '/auth-client/account', method: 'GET' })
      profile.value = res
      persistProfile()
      return res
    } catch {
      return null
    }
  }

  /** 发送邮箱验证码（注册/登录通用） */
  async function emailSendCode(email: string): Promise<void> {
    await request<void>({ url: '/auth-client/email/send-code', method: 'POST', data: { email } })
  }

  /** 邮箱验证码注册：成功后写入 token + profile */
  async function emailRegister(
    email: string,
    code: string,
    captchaId: string,
    captchaCode: string,
    nickname?: string,
  ): Promise<void> {
    const res = await request<{ accessToken: string, user: IClientUser }>({
      url: '/auth-client/email/register',
      method: 'POST',
      data: { email, code, captchaId, captchaCode, nickname },
    })
    setToken(res.accessToken)
    profile.value = res.user
    persistProfile()
  }

  /** 邮箱验证码登录：成功后写入 token + profile */
  async function emailLogin(email: string, code: string): Promise<void> {
    const res = await request<{ accessToken: string, user: IClientUser }>({
      url: '/auth-client/email/login',
      method: 'POST',
      data: { email, code },
    })
    setToken(res.accessToken)
    profile.value = res.user
    persistProfile()
  }

  /** 邮箱 + 密码登录：成功后写入 token + profile */
  async function emailPasswordLogin(
    email: string,
    password: string,
    captchaId: string,
    captchaCode: string,
  ): Promise<void> {
    const res = await request<{ accessToken: string, user: IClientUser }>({
      url: '/auth-client/email/password-login',
      method: 'POST',
      data: { email, password, captchaId, captchaCode },
    })
    setToken(res.accessToken)
    profile.value = res.user
    persistProfile()
  }

  /** 忘记密码：邮箱验证码重置密码 */
  async function emailResetPassword(email: string, code: string, newPassword: string): Promise<void> {
    await request<void>({
      url: '/auth-client/email/reset-password',
      method: 'POST',
      data: { email, code, newPassword },
    })
  }

  /** 已登录用户设置 / 修改密码 */
  async function passwordSet(oldPassword: string | undefined, newPassword: string): Promise<void> {
    await request<void>({
      url: '/auth-client/password/set',
      method: 'POST',
      data: { oldPassword, newPassword },
    })
  }

  return {
    token, profile, setToken, logout, isLogged,
    loginWithToken, fetchProfile, emailSendCode,
    emailRegister, emailLogin, emailPasswordLogin,
    emailResetPassword, passwordSet,
  }
})